/**
 * Video Extract Audio utility functions
 * Uses @ffmpeg/ffmpeg for client-side video processing
 */

import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'

let ffmpeg: FFmpeg | null = null
let ffmpegLoaded = false

export interface ExtractOptions {
  format: 'mp3' | 'wav' | 'aac' | 'ogg'
  quality: 'low' | 'medium' | 'high'
}

export interface VideoMetadata {
  duration: number
  width: number
  height: number
  codec: string
  hasAudio: boolean
}

export interface ProgressCallback {
  (progress: number, message: string): void
}

const QUALITY_SETTINGS = {
  mp3: {
    low: '128k',
    medium: '192k',
    high: '320k',
  },
  aac: {
    low: '96k',
    medium: '128k',
    high: '256k',
  },
  wav: {
    low: '16000',
    medium: '44100',
    high: '48000',
  },
  ogg: {
    low: '128k',
    medium: '192k',
    high: '256k',
  },
}

/**
 * Initialize FFmpeg instance
 */
export async function initializeFFmpeg(onProgress?: ProgressCallback): Promise<void> {
  if (ffmpegLoaded && ffmpeg) {
    return
  }

  try {
    onProgress?.(10, 'Loading FFmpeg core...')

    ffmpeg = new FFmpeg()

    ffmpeg.on('log', ({ message }) => {
      console.log('[FFmpeg]:', message)
    })

    ffmpeg.on('progress', ({ progress }) => {
      onProgress?.(10 + progress * 80, 'Processing video...')
    })

    onProgress?.(30, 'Loading FFmpeg WASM...')

    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm'

    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    })

    ffmpegLoaded = true
    onProgress?.(100, 'FFmpeg ready!')
  } catch (error) {
    ffmpeg?.terminate()
    ffmpeg = null
    ffmpegLoaded = false
    console.error('Failed to load FFmpeg:', error)
    throw new Error('Failed to initialize video processor. Please refresh and try again.')
  }
}

/**
 * Get FFmpeg instance
 */
function getFFmpeg(): FFmpeg {
  if (!ffmpeg || !ffmpegLoaded) {
    throw new Error('FFmpeg not initialized. Call initializeFFmpeg() first.')
  }
  return ffmpeg
}

/**
 * Extract audio from video file
 */
export async function extractAudio(
  file: File,
  options: ExtractOptions,
  onProgress?: ProgressCallback
): Promise<Blob> {
  const ff = getFFmpeg()

  try {
    onProgress?.(0, 'Reading video file...')

    const inputName = 'input' + getFileExtension(file.name)
    await ff.writeFile(inputName, await fetchFile(file))

    onProgress?.(20, 'Extracting audio...')

    const args: string[] = ['-i', inputName]

    // Build output settings based on format and quality
    const outputName = `output.${options.format}`

    switch (options.format) {
      case 'mp3':
        args.push(
          '-vn', // no video
          '-c:a', 'libmp3lame',
          '-b:a', QUALITY_SETTINGS.mp3[options.quality],
          '-ar', '44100',
          outputName
        )
        break

      case 'wav':
        args.push(
          '-vn',
          '-c:a', 'pcm_s16le',
          '-ar', QUALITY_SETTINGS.wav[options.quality],
          outputName
        )
        break

      case 'aac':
        args.push(
          '-vn',
          '-c:a', 'aac',
          '-b:a', QUALITY_SETTINGS.aac[options.quality],
          '-ar', '44100',
          outputName
        )
        break

      case 'ogg':
        args.push(
          '-vn',
          '-c:a', 'libvorbis',
          '-b:a', QUALITY_SETTINGS.ogg[options.quality],
          '-ar', '44100',
          outputName
        )
        break
    }

    onProgress?.(30, 'Processing audio extraction...')

    await ff.exec(args)

    onProgress?.(90, 'Reading output...')

    const data = await ff.readFile(outputName)
    const mimeTypes = {
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      aac: 'audio/aac',
      ogg: 'audio/ogg',
    }
    const blob = new Blob([data], { type: mimeTypes[options.format] })

    // Cleanup
    await ff.deleteFile(inputName)
    await ff.deleteFile(outputName)

    onProgress?.(100, 'Complete!')

    return blob
  } catch (error) {
    console.error('Audio extraction error:', error)
    throw new Error('Failed to extract audio. Please check your video file and try again.')
  }
}

/**
 * Get video metadata using Web API
 */
export async function getVideoMetadata(file: File): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const url = URL.createObjectURL(file)

    video.addEventListener('loadedmetadata', () => {
      const metadata: VideoMetadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        codec: '',
        hasAudio: true, // Assume yes, we'll verify in FFmpeg
      }

      URL.revokeObjectURL(url)
      resolve(metadata)
    })

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load video metadata'))
    })

    video.src = url
  })
}

/**
 * Generate video thumbnail
 */
export async function generateThumbnail(file: File, timeSeconds: number = 0): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const url = URL.createObjectURL(file)

    video.addEventListener('loadedmetadata', () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
    })

    video.addEventListener('seeked', () => {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const thumbnail = canvas.toDataURL('image/jpeg', 0.8)
        URL.revokeObjectURL(url)
        resolve(thumbnail)
      } else {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to create canvas context'))
      }
    })

    video.addEventListener('error', () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to generate thumbnail'))
    })

    video.src = url
    video.currentTime = timeSeconds
  })
}

/**
 * Download audio file
 */
export function downloadAudio(blob: Blob, originalFilename: string, format: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const baseName = originalFilename.replace(/\.[^/.]+$/, '')
  a.href = url
  a.download = `${baseName}_audio.${format}`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Format duration in seconds to HH:MM:SS
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): string {
  const ext = filename.substring(filename.lastIndexOf('.'))
  return ext || '.mp4'
}

/**
 * Validate video file
 */
export function validateVideoFile(file: File): { valid: boolean; error?: string } {
  const validTypes = [
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'video/x-msvideo',
    'video/avi',
  ]
  const validExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv']

  const hasValidType = validTypes.includes(file.type)
  const hasValidExtension = validExtensions.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  )

  if (!hasValidType && !hasValidExtension) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload MP4, WebM, MOV, or AVI files.',
    }
  }

  const maxSize = 1024 * 1024 * 1024 // 1GB
  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File too large. Maximum size is 1GB.',
    }
  }

  return { valid: true }
}
